import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../../PageHeader/PageHeader';
import { HitApi } from '../../../Api/ApiHit';
import { searchStock, addStockTransaction } from '../../../constant/Constant';
import { getSelectedUnit } from '../../../storage/Storage';
import toast from 'react-hot-toast';

function TransferStock({ isModal = false, onClose, onTransferComplete, productType = null, fromLocation = null }) {
    const navigate = useNavigate();
    
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingStocks, setIsLoadingStocks] = useState(false);
    const [stocks, setStocks] = useState([]);
    const [selectedStocks, setSelectedStocks] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredStocks, setFilteredStocks] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [searchTimeout, setSearchTimeout] = useState(null);
    const [transferData, setTransferData] = useState({
        fromLocation: fromLocation || 'warehouse',
        toLocation: fromLocation === 'salon' ? 'warehouse' : 'salon',
        transferQuantity: '',
        reason: '',
        notes: ''
    });

    // Update transferData when fromLocation prop changes
    useEffect(() => {
        if (fromLocation) {
            setTransferData(prev => ({
                ...prev,
                fromLocation: fromLocation,
                toLocation: fromLocation === 'salon' ? 'warehouse' : 'salon'
            }));
        }
    }, [fromLocation]);

    // Fetch stocks from current location
    useEffect(() => {
        // Clear search when location changes
        setSearchTerm('');
        setSelectedStocks([]);
        if (searchTimeout) {
            clearTimeout(searchTimeout);
        }
        fetchStocks();
    }, [transferData.fromLocation]);

    // Cleanup search timeout
    useEffect(() => {
        return () => {
            if (searchTimeout) {
                clearTimeout(searchTimeout);
            }
        };
    }, [searchTimeout]);

    const fetchStocks = useCallback(async () => {
        setIsLoadingStocks(true);
        try {
            const searchObj = {
                unitId: getSelectedUnit()?._id,
                isActive: true,
                stockLocation: transferData.fromLocation
            };

            // Add productType filter if specified
            if (productType) {
                searchObj.type = productType;
            }

            const json = {
                page: 1,
                limit: 100,
                search: searchObj,
                populate: ['productId']
            };

            const res = await HitApi(json, searchStock);
            
            if (res?.statusCode === 200) {
                const stockData = res?.data?.docs || res?.stocks || res?.result || [];
                
                // Transform and filter stocks with available quantity
                const availableStocks = stockData
                    .map(stock => ({
                        _id: stock._id,
                        productId: stock.productId?._id || stock.productId,
                        productName: stock.productId?.productName || 'Unknown Product',
                        brand: stock.productId?.brand || '',
                        productType: stock.productId?.productType || '',
                        productImageUrl: stock.productId?.productImageUrl || '',
                        availableQuantity: stock.qty || 0, // Use qty field as available quantity
                        qty: stock.qty || 0,
                        stockIn: stock.stockIn || 0,
                        stockOut: stock.stockOut || 0,
                        stockLocation: stock.stockLocation,
                        vendor: stock.productId?.vendor || stock.type || '',
                        type: stock.type || 'professional'
                    }))
                    .filter(stock => stock.availableQuantity > 0); // Only show stocks with available quantity

                setStocks(availableStocks);
                setFilteredStocks(availableStocks);
            } else {
                toast.error('Failed to fetch stocks');
                setStocks([]);
                setFilteredStocks([]);
            }
        } catch (error) {
            console.error('Error fetching stocks:', error);
            toast.error('Error loading stocks');
            setStocks([]);
            setFilteredStocks([]);
        } finally {
            setIsLoadingStocks(false);
        }
    }, [transferData.fromLocation, productType, fromLocation]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setTransferData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const searchStocksFromAPI = async (searchTerm) => {
        setIsSearching(true);
        try {
            console.log('🔍 SEARCHING STOCKS:', {
                searchTerm,
                fromLocation: transferData.fromLocation,
                unitId: getSelectedUnit()?._id
            });

            // Use client-side filtering instead of server-side search for better compatibility
            const searchObj = {
                unitId: getSelectedUnit()?._id,
                isActive: true,
                stockLocation: transferData.fromLocation
            };

            // Add productType filter if specified
            if (productType) {
                searchObj.type = productType;
            }

            const json = {
                page: 1,
                limit: 500, // Get more results for better client-side filtering
                search: searchObj,
                populate: ['productId']
            };

            console.log('🔍 SEARCH API REQUEST:', json);

            const res = await HitApi(json, searchStock);
            
            console.log('🔍 SEARCH API RESPONSE:', {
                statusCode: res?.statusCode,
                dataLength: res?.data?.docs?.length || 0,
                total: res?.data?.totalDocs || 0
            });
            
            if (res?.statusCode === 200) {
                const stockData = res?.data?.docs || res?.stocks || res?.result || [];
                
                const availableStocks = stockData
                    .map(stock => ({
                        _id: stock._id,
                        productId: stock.productId?._id || stock.productId,
                        productName: stock.productId?.productName || 'Unknown Product',
                        brand: stock.productId?.brand || '',
                        productType: stock.productId?.productType || '',
                        productImageUrl: stock.productId?.productImageUrl || '',
                        availableQuantity: stock.qty || 0, // Use qty field as available quantity
                        qty: stock.qty || 0,
                        stockIn: stock.stockIn || 0,
                        stockOut: stock.stockOut || 0,
                        stockLocation: stock.stockLocation,
                        vendor: stock.productId?.vendor || stock.type || '',
                        type: stock.type || 'professional'
                    }))
                    .filter(stock => stock.availableQuantity > 0)
                    // Client-side filtering for search term
                    .filter(stock => {
                        const searchLower = searchTerm.toLowerCase();
                        return (
                            stock.productName.toLowerCase().includes(searchLower) ||
                            stock.brand.toLowerCase().includes(searchLower) ||
                            stock.productType.toLowerCase().includes(searchLower) ||
                            stock.vendor.toLowerCase().includes(searchLower)
                        );
                    });

                console.log('🔍 FILTERED RESULTS:', {
                    totalStocks: stockData.length,
                    availableStocks: stockData.filter(s => (s.qty || 0) > 0).length,
                    filteredResults: availableStocks.length,
                    searchTerm
                });

                setFilteredStocks(availableStocks);
            } else {
                console.error('🔍 SEARCH API ERROR:', res?.message || 'Unknown error');
                setFilteredStocks([]);
            }
        } catch (error) {
            console.error('🔍 SEARCH ERROR:', error);
            setFilteredStocks([]);
        } finally {
            setIsSearching(false);
        }
    };

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchTerm(value);
        
        // Clear existing timeout
        if (searchTimeout) {
            clearTimeout(searchTimeout);
        }
        
        if (value.trim() === '') {
            setFilteredStocks(stocks); // Show all stocks
            return;
        }

        // Use API search with debounce for live search
        const timeout = setTimeout(() => {
            searchStocksFromAPI(value.trim());
        }, 150);
        setSearchTimeout(timeout);
    };

    const handleClearSearch = () => {
        setSearchTerm('');
        setFilteredStocks(stocks);
        if (searchTimeout) {
            clearTimeout(searchTimeout);
        }
    };

    const handleStockSelect = (stock, isSelected) => {
        if (isSelected) {
            setSelectedStocks(prev => [...prev, { ...stock, transferQuantity: 1 }]);
        } else {
            setSelectedStocks(prev => prev.filter(s => s._id !== stock._id));
        }
    };

    const handleQuantityChange = (stockId, quantity) => {
        const numQuantity = parseInt(quantity) || 0;
        setSelectedStocks(prev =>
            prev.map(stock =>
                stock._id === stockId 
                    ? { ...stock, transferQuantity: numQuantity }
                    : stock
            )
        );
    };

    const handleTransfer = async () => {
        if (selectedStocks.length === 0) {
            toast.error('Please select at least one product to transfer');
            return;
        }

        const invalidQuantities = selectedStocks.filter(
            stock => stock.transferQuantity <= 0 || stock.transferQuantity > stock.availableQuantity
        );

        if (invalidQuantities.length > 0) {
            toast.error('Please enter valid transfer quantities');
            return;
        }

        if (!transferData.reason.trim()) {
            toast.error('Please provide a reason for transfer');
            return;
        }

        setIsLoading(true);

        console.log('🚀 STARTING STOCK TRANSFER:', {
            transferData: {
                fromLocation: transferData.fromLocation,
                toLocation: transferData.toLocation,
                reason: transferData.reason,
                notes: transferData.notes
            },
            selectedStocks: selectedStocks.map(stock => ({
                stockId: stock._id,
                productId: stock.productId,
                productName: stock.productName,
                transferQuantity: stock.transferQuantity,
                currentStockOut: stock.stockOut,
                availableQuantity: stock.availableQuantity,
                type: stock.type
            })),
            totalStocks: selectedStocks.length,
            totalQuantity: selectedStocks.reduce((sum, stock) => sum + stock.transferQuantity, 0)
        });

        try {
            let successCount = 0;
            let errorCount = 0;

            for (const stock of selectedStocks) {
                try {
                    // Create stock transaction entry with timestamp to avoid duplicate key errors
                    const currentTime = new Date();
                    const stockTransactionData = {
                        stockId: stock._id,
                        warehouseId: getSelectedUnit()?._id,
                        productId: stock.productId,
                        unitId: getSelectedUnit()?._id,
                        type: stock.type,
                        status: 'transferd', // Required field: 'transferd', 'recevied', 'instock'
                        place: transferData.toLocation === 'salon' ? 'salon' : 'warehouse',
                        stockLocation: transferData.toLocation === 'brand' ? 'salon' : transferData.toLocation, // Map brand to salon for now
                        stockIn: parseInt(stock.transferQuantity) || 0,
                        transferQuantity: parseInt(stock.transferQuantity) || 0,
                        stockOut: 0,
                        stockAlert: parseInt(stock.stockAlert) || 0,
                        isActive: true,
                        transactionReason: transferData.reason || '',
                        transferFrom: transferData.fromLocation || '',
                        transferNotes: transferData.notes || '',
                        // Add unique identifier to avoid duplicate key constraint
                        transactionId: `${stock._id}_${currentTime.getTime()}`,
                        transactionDate: currentTime
                    };

                    console.log('🔄 ADD STOCK TRANSACTION REQUEST:', {
                        stockId: stock._id,
                        productName: stock.productName,
                        fromLocation: transferData.fromLocation,
                        toLocation: transferData.toLocation,
                        transferQuantity: stock.transferQuantity,
                        reason: transferData.reason,
                        notes: transferData.notes,
                        status: stockTransactionData.status,
                        endpoint: addStockTransaction,
                        method: 'POST'
                    });

                    console.log('📤 ACTUAL PAYLOAD BEING SENT:', JSON.stringify(stockTransactionData, null, 2));

                    const transferRes = await HitApi(stockTransactionData, addStockTransaction);
                    console.log('✅ STOCK TRANSACTION RESPONSE:', {
                        stockId: stock._id,
                        productName: stock.productName,
                        response: transferRes
                    });

                    if (transferRes?.success || transferRes?.statusCode === 200) {
                        successCount++;
                    } else {
                        errorCount++;
                        console.error(`Failed to transfer ${stock.productName}:`, transferRes?.message || 'Unknown error');
                    }
                } catch (error) {
                    errorCount++;
                    console.error(`Error transferring ${stock.productName}:`, error);
                }
            }

            if (successCount > 0) {
                toast.success(`${successCount} products transferred successfully!`);
                if (errorCount === 0) {
                    if (isModal && onTransferComplete) {
                        onTransferComplete();
                    } else {
                        navigate('/warehouse');
                    }
                } else {
                    toast.error(`${errorCount} products failed to transfer`);
                    // Refresh stocks list
                    fetchStocks();
                    setSelectedStocks([]);
                }
            } else {
                toast.error('Failed to transfer any products');
            }
        } catch (error) {
            console.error('Error during transfer:', error);
            toast.error('Transfer failed. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleCancel = () => {
        if (isModal && onClose) {
            onClose();
        } else {
            navigate('/warehouse');
        }
    };

    const getTotalTransferItems = () => {
        return selectedStocks.reduce((sum, stock) => sum + stock.transferQuantity, 0);
    };

    const modalContent = (
        <div className="p-5">
            <PageHeader
                title="Transfer Stock"
                description="Transfer products between warehouse, salon, and brand locations. Search products by name, brand, or type."
            />

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Transfer Configuration */}
                <div className="lg:col-span-1">
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                        <h3 className="text-lg font-medium text-gray-900 mb-4">Transfer Details</h3>
                        
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    From Location
                                </label>
                                <select
                                    name="fromLocation"
                                    value={transferData.fromLocation}
                                    onChange={handleInputChange}
                                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    disabled={isLoading}
                                >
                                    <option value="warehouse">Warehouse</option>
                                    <option value="salon">Salon</option>
                                    <option value="brand">Brand</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    To Location
                                </label>
                                <select
                                    name="toLocation"
                                    value={transferData.toLocation}
                                    onChange={handleInputChange}
                                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    disabled={isLoading}
                                >
                                    <option value="salon">Salon</option>
                                    <option value="warehouse">Warehouse</option>
                                    <option value="brand">Brand</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Transfer Reason *
                                </label>
                                <select
                                    name="reason"
                                    value={transferData.reason}
                                    onChange={handleInputChange}
                                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    required
                                    disabled={isLoading}
                                >
                                    <option value="">Select reason</option>
                                    <option value="stock_replenishment">Stock Replenishment</option>
                                    <option value="customer_demand">Customer Demand</option>
                                    <option value="rebalancing">Stock Rebalancing</option>
                                    <option value="salon_requirement">Salon Requirement</option>
                                    <option value="maintenance">Maintenance</option>
                                    <option value="other">Other</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Notes (Optional)
                                </label>
                                <textarea
                                    name="notes"
                                    value={transferData.notes}
                                    onChange={handleInputChange}
                                    rows="3"
                                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    placeholder="Additional notes..."
                                    disabled={isLoading}
                                />
                            </div>

                            {/* Transfer Summary */}
                            {selectedStocks.length > 0 && (
                                <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                                    <h4 className="font-medium text-blue-900 mb-2">Transfer Summary</h4>
                                    <div className="text-sm text-blue-800">
                                        <div>Products: {selectedStocks.length}</div>
                                        <div>Total Items: {getTotalTransferItems()}</div>
                                        <div className="mt-2">
                                            <span className="font-medium">From:</span> {transferData.fromLocation === 'warehouse' ? 'Warehouse' : transferData.fromLocation === 'brand' ? 'Brand' : 'Salon'}
                                        </div>
                                        <div>
                                            <span className="font-medium">To:</span> {transferData.toLocation === 'warehouse' ? 'Warehouse' : transferData.toLocation === 'brand' ? 'Brand' : 'Salon'}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Stock Selection */}
                <div className="lg:col-span-2">
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-medium text-gray-900">
                                Available Stock ({transferData.fromLocation === 'warehouse' ? 'Warehouse' : transferData.fromLocation === 'brand' ? 'Brand' : 'Salon'})
                            </h3>
                            <div className="text-sm text-gray-500">
                                {searchTerm ? `${filteredStocks.length} of ${stocks.length} products` : `${stocks.length} products available`}
                            </div>
                        </div>

                        {/* Search Input */}
                        <div className="mb-4">
                            <div className="relative">
                                <input
                                    type="text"
                                    value={searchTerm}
                                    onChange={handleSearchChange}
                                    className="w-full p-3 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    placeholder={
                                        isLoadingStocks ? 'Loading products...' :
                                        isSearching ? 'Searching...' :
                                        'Search products by name, brand, or type...'
                                    }
                                    disabled={isLoadingStocks || isLoading}
                                />
                                {searchTerm && (
                                    <button
                                        onClick={handleClearSearch}
                                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                        disabled={isLoadingStocks || isLoading}
                                    >
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </button>
                                )}
                                {isSearching && (
                                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                                        <div className="animate-spin h-5 w-5 border-2 border-blue-500 border-t-transparent rounded-full"></div>
                                    </div>
                                )}
                            </div>
                            {searchTerm && (
                                <div className="mt-1 text-xs text-gray-500">
                                    {isSearching ? 'Searching...' : 
                                     filteredStocks.length === 0 ? `No products found for "${searchTerm}"` :
                                     `Showing ${filteredStocks.length} results for "${searchTerm}"`}
                                </div>
                            )}
                        </div>

                        {isLoadingStocks ? (
                            <div className="flex justify-center items-center h-32">
                                <div className="flex items-center space-x-2">
                                    <div className="animate-spin h-6 w-6 border-2 border-blue-500 border-t-transparent rounded-full"></div>
                                    <span className="text-gray-600">Loading stocks...</span>
                                </div>
                            </div>
                        ) : filteredStocks.length === 0 ? (
                            <div className="text-center py-8 text-gray-500">
                                <div className="text-4xl mb-4">📦</div>
                                {searchTerm ? (
                                    <>
                                        <p>No products found for "{searchTerm}"</p>
                                        <p className="text-sm">Try adjusting your search terms</p>
                                        <button
                                            onClick={handleClearSearch}
                                            className="mt-2 text-blue-600 hover:text-blue-800 text-sm"
                                        >
                                            Clear search
                                        </button>
                                    </>
                                ) : (
                                    <>
                                        <p>No products available for transfer</p>
                                        <p className="text-sm">Try changing the source location</p>
                                    </>
                                )}
                            </div>
                        ) : (
                            <div className="max-h-96 overflow-y-auto">
                                <table className="w-full text-sm">
                                    <thead className="bg-gray-50 sticky top-0">
                                        <tr className="border-b">
                                            <th className="text-left py-3 px-3 font-medium">Select</th>
                                            <th className="text-left py-3 px-3 font-medium">Product</th>
                                            <th className="text-center py-3 px-2 font-medium">Available</th>
                                            <th className="text-center py-3 px-2 font-medium">Transfer Qty</th>
                                            <th className="text-center py-3 px-2 font-medium">Type</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredStocks.map((stock) => {
                                            const isSelected = selectedStocks.some(s => s._id === stock._id);
                                            const selectedStock = selectedStocks.find(s => s._id === stock._id);
                                            
                                            return (
                                                <tr 
                                                    key={stock._id}
                                                    className={`border-b hover:bg-gray-50 ${isSelected ? 'bg-blue-50' : ''}`}
                                                >
                                                    <td className="py-3 px-3">
                                                        <input
                                                            type="checkbox"
                                                            checked={isSelected}
                                                            onChange={(e) => handleStockSelect(stock, e.target.checked)}
                                                            className="w-4 h-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                                            disabled={isLoading}
                                                        />
                                                    </td>
                                                    <td className="py-3 px-3">
                                                        <div className="flex items-center">
                                                            {stock.productImageUrl ? (
                                                                <img
                                                                    src={stock.productImageUrl}
                                                                    alt={stock.productName}
                                                                    className="w-8 h-8 rounded mr-3 object-cover"
                                                                    onError={(e) => {
                                                                        e.target.style.display = 'none';
                                                                        e.target.nextSibling.style.display = 'flex';
                                                                    }}
                                                                />
                                                            ) : null}
                                                            <div className="w-8 h-8 rounded bg-gray-200 flex items-center justify-center mr-3 text-xs"
                                                                style={{ display: stock.productImageUrl ? 'none' : 'flex' }}>
                                                                📦
                                                            </div>
                                                            <div>
                                                                <div className="font-medium text-gray-900">
                                                                    {stock.productName}
                                                                </div>
                                                                {stock.brand && (
                                                                    <div className="text-xs text-gray-500">
                                                                        {stock.brand}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="py-3 px-2 text-center">
                                                        <span className={`font-medium px-2 py-1 rounded text-xs ${
                                                            stock.availableQuantity <= 0 ? 'bg-red-100 text-red-800' :
                                                            stock.availableQuantity < 10 ? 'bg-yellow-100 text-yellow-800' :
                                                            'bg-green-100 text-green-800'
                                                        }`}>
                                                            {stock.availableQuantity}
                                                        </span>
                                                    </td>
                                                    <td className="py-3 px-2 text-center">
                                                        {isSelected ? (
                                                            <input
                                                                type="number"
                                                                min="1"
                                                                max={stock.availableQuantity}
                                                                value={selectedStock?.transferQuantity || 1}
                                                                onChange={(e) => handleQuantityChange(stock._id, e.target.value)}
                                                                className="w-16 p-1 border border-gray-300 rounded text-center focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                                disabled={isLoading}
                                                            />
                                                        ) : (
                                                            <span className="text-gray-400">-</span>
                                                        )}
                                                    </td>
                                                    <td className="py-3 px-2 text-center">
                                                        <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                                                            stock.vendor?.toLowerCase() === 'professional' 
                                                                ? 'bg-purple-100 text-purple-800' 
                                                                : 'bg-orange-100 text-orange-800'
                                                        }`}>
                                                            {stock.vendor || stock.type}
                                                        </span>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Action Buttons */}
            <div className="mt-6 flex justify-between">
                <button
                    onClick={handleCancel}
                    className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500"
                    disabled={isLoading}
                >
                    Cancel
                </button>
                <button
                    onClick={handleTransfer}
                    className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50"
                    disabled={isLoading || selectedStocks.length === 0 || !transferData.reason}
                >
                    {isLoading ? 'Transferring...' : `Transfer ${selectedStocks.length} Products`}
                </button>
            </div>
        </div>
    );


    if (isModal) {
        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
                    <div className="flex items-center justify-between p-4 border-b border-gray-200">
                        <h2 className="text-lg font-semibold text-gray-900">Transfer Stock</h2>
                        <button
                            onClick={handleCancel}
                            className="text-gray-400 hover:text-gray-600 focus:outline-none"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                    <div className="p-0">
                        {modalContent}
                    </div>
                </div>
            </div>
        );
    }

    return modalContent;
}

export default TransferStock;